import * as React from 'react';
import type { Editor } from '@tiptap/react';
import type {
  CommentAuthor,
  CommentReply,
} from '../../../../ui/tiptap/extensions/comment-mark';

export interface CommentThreadActions {
  /** Appends a reply and reopens the thread — answering a resolved note un-resolves it. */
  addReply: (commentId: string, replies: CommentReply[], body: string) => void;
  toggleResolved: (
    commentId: string,
    replies: CommentReply[],
    resolved: boolean
  ) => void;
  /**
   * Rewrites one message in a thread, leaving the rest of it and the thread's
   * resolved state alone.
   */
  editReply: (args: {
    commentId: string;
    replies: CommentReply[];
    resolved: boolean;
    replyId: string;
    body: string;
  }) => void;
  /**
   * Drops one message from a thread. Removing the message that opened it takes the
   * whole thread with it — the replies were answers to it, and an anchor whose first
   * message is gone is a conversation starting mid-sentence.
   */
  removeReply: (args: {
    commentId: string;
    replies: CommentReply[];
    resolved: boolean;
    replyId: string;
  }) => void;
  remove: (commentId: string) => void;
  /**
   * Annotates the current selection with a brand-new thread and collapses the
   * selection into it, so the new thread becomes the active one. Returns false when
   * there is nothing selected to anchor to.
   */
  create: (body: string) => boolean;
}

/**
 * The four mutations a comment thread supports, in one place.
 *
 * Shared by the floating {@link CommentThreadPopover} and the comments rail: the two
 * surfaces disagree about layout, not about what resolving a thread means, and a
 * second copy of these chains is how they would drift.
 */
export function useCommentThreadActions(
  editor: Editor | null,
  author: CommentAuthor | null
): CommentThreadActions {
  return React.useMemo<CommentThreadActions>(
    () => ({
      addReply: (commentId, replies, body) => {
        if (editor === null || author === null) {
          return;
        }
        const trimmed = body.trim();
        if (trimmed === '') {
          return;
        }
        const reply: CommentReply = {
          id: crypto.randomUUID(),
          authorId: author.id,
          authorName: author.name,
          body: trimmed,
          createdAt: new Date().toISOString(),
        };
        editor.commands.updateComment(commentId, {
          replies: [...replies, reply],
          resolved: false,
        });
      },

      toggleResolved: (commentId, replies, resolved) => {
        if (editor === null) {
          return;
        }
        editor.commands.updateComment(commentId, {
          replies,
          resolved: !resolved,
        });
      },

      editReply: ({ commentId, replies, resolved, replyId, body }) => {
        if (editor === null) {
          return;
        }
        const trimmed = body.trim();
        if (trimmed === '') {
          return;
        }
        const next = replies.map((reply) =>
          reply.id === replyId ? { ...reply, body: trimmed } : reply
        );
        // `resolved` is passed through rather than cleared: fixing a typo in a
        // settled thread is not reopening the conversation, which is what a reply
        // would be.
        editor.commands.updateComment(commentId, { replies: next, resolved });
      },

      removeReply: ({ commentId, replies, resolved, replyId }) => {
        if (editor === null) {
          return;
        }
        if (replies[0]?.id === replyId) {
          editor.commands.removeComment(commentId);
          return;
        }
        const next = replies.filter((reply) => reply.id !== replyId);
        editor.commands.updateComment(commentId, { replies: next, resolved });
      },

      remove: (commentId) => {
        editor?.commands.removeComment(commentId);
      },

      create: (body) => {
        if (editor === null || author === null) {
          return false;
        }
        const trimmed = body.trim();
        if (trimmed === '' || editor.state.selection.empty) {
          return false;
        }
        const reply: CommentReply = {
          id: crypto.randomUUID(),
          authorId: author.id,
          authorName: author.name,
          body: trimmed,
          createdAt: new Date().toISOString(),
        };
        // Collapse *inside* the annotated range, not onto its end. The mark is
        // `inclusive: false`, so a caret parked exactly on the closing boundary
        // resolves to the unmarked side and the thread that was just written would
        // come back as "no active comment" — no highlight, no expanded card, no
        // popover. One character in is the same position `scrollToCommentThread`
        // picks. A single-character anchor has no interior, so it keeps the end.
        const { from, to } = editor.state.selection;
        const caret = to - from > 1 ? from + 1 : to;
        return editor
          .chain()
          .setComment({
            commentId: crypto.randomUUID(),
            replies: [reply],
            resolved: false,
          })
          .setTextSelection(caret)
          .run();
      },
    }),
    [author, editor]
  );
}
